import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {InputDate} from '@/components/commons/input-date-legacy/InputDate'
import {InputSelectCity} from '@/components/commons/input-select-city/InputSelectCity'
import {InputSelectCountry} from '@/components/commons/input-select-country/InputSelectCountry'
import {InputSelectProvince} from '@/components/commons/input-select-province/InputSelectProvince'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {InputText} from '@/components/commons/input-text/InputText'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {GenerateTaxCodeButton, InputTaxCode} from '@/components/ui/form-atoms/FormAtoms'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {formatDate} from '@/dayjs'
import {useAsync} from '@/hooks/useAsync'
import {httpGetTaxCode} from '@/services/personalData.http'
import {useGetCountries} from '@/services/useGetCountries'
import {useTranslation} from '@/translations/i18n'
import {Country} from '@/types'
import {toast} from '@/utilities/toast/toast'
import {AxiosError} from 'axios'
import dayjs from 'dayjs'
import {useCallback, useEffect, useMemo} from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {PatientPersonalDataFormValidationSchema} from '@/features/patients/types'
import {PatientTaxCodeField} from '@/features/patients/components/patient-tax-code-field/PatientTaxCodeField'
import {patientOccupationToLabel, patientEducationToLabel} from '@/features/patients/utils'
import {maritalStatusesToLabel} from '@/utilities/helpers'

export const PatientPersonalDataForm = () => {
    const {t} = useTranslation()

    // initial queries
    const defaultCountryQuery = useGetCountries({search: 'Italia', limit: 1}, {staleTime: Infinity})

    // methods
    const form = useFormContext<PatientPersonalDataFormValidationSchema>()
    const calculatePatientTaxCode = useAsync()

    // form values
    const isBornAbroad = useWatch({name: 'isBornAbroad', control: form.control})
    const birthProvince = useWatch({name: 'birthProvince', control: form.control})
    const firstName = useWatch({name: 'firstName', control: form.control})
    const lastName = useWatch({name: 'lastName', control: form.control})
    const birthDate = useWatch({name: 'birthDate', control: form.control})
    const birthCountryId = useWatch({name: 'birthCountry.id', control: form.control})
    const birthCityId = useWatch({name: 'birthCity.id', control: form.control})
    const gender = useWatch({name: 'gender', control: form.control})
    const citizenship = useWatch({name: 'citizenship', control: form.control})

    // booleans
    const isCalculateTaxCodeDisabled = useMemo(
        () => !firstName || !lastName || !gender || !birthDate || !birthCountryId || (!isBornAbroad && !birthCityId),
        [firstName, lastName, gender, birthDate, birthCountryId, isBornAbroad, birthProvince, birthCityId]
    )

    // actions
    const setInitialValues = useCallback((country: Country) => {
        form.setValue('birthCountry', country, {shouldDirty: false})
        form.setValue('citizenship', country, {shouldDirty: false})
    }, [])

    const onCalculateUserTaxCode = async () => {
        try {
            const {data} = await calculatePatientTaxCode.run(
                httpGetTaxCode({
                    firstName,
                    lastName,
                    gender,
                    birthDate: formatDate(birthDate.toISOString(), 'YYYY-MM-DD'),
                    birthCountryId,
                    birthCityId: isBornAbroad ? null : birthCityId
                })
            )

            if (data.cf) {
                form.setValue('taxCode', data.cf, {shouldDirty: true})
                form.clearErrors('taxCode')
            } else {
                throw new Error(t('errors:tax_code'))
            }
        } catch (error: unknown) {
            const axiosError = error as AxiosError<{code: string}>
            if (axiosError.response?.data?.code == 'taxcode-not-found') {
                form.setError('taxCode', {message: t('errors:tax_code_not_found')})
            }

            toast.error(t('errors:tax_code'))
        }
    }

    // Init country value
    useEffect(() => {
        if (defaultCountryQuery.remappedData?.[0] && !birthCountryId) {
            setInitialValues(defaultCountryQuery.remappedData[0])
        }
    }, [defaultCountryQuery.remappedData?.[0]])

    return (
        <Flexbox gap={4} direction="column" fullWidth>
            <Flexbox gap={4} fullWidth>
                <InputText
                    type="text"
                    label={t('commons:last_name_label')}
                    placeholder={t('commons:last_name_placeholder')}
                    errorMessage={form.formState.errors.lastName?.message}
                    maxLength={50}
                    {...form.register('lastName', {
                        onChange: () => form.setValue('taxCode', '')
                    })}
                />
                <InputText
                    type="text"
                    label={t('commons:first_name_label')}
                    placeholder={t('commons:first_name_placeholder')}
                    maxLength={50}
                    errorMessage={form.formState.errors.firstName?.message}
                    {...form.register('firstName', {onChange: () => form.setValue('taxCode', '')})}
                />
            </Flexbox>

            <Flexbox direction="column" gap={1.5}>
                <Label>{t('commons:gender_label')}</Label>
                <Flexbox direction="column" gap={1.5}>
                    <Flexbox gap={4}>
                        <RadioField
                            value={'male'}
                            label={t('commons:gender_male')}
                            {...form.register('gender', {
                                onChange: () => {
                                    form.setValue('taxCode', '')
                                    form.trigger('gender')
                                }
                            })}
                        />
                        <RadioField
                            value={'female'}
                            label={t('commons:gender_female')}
                            {...form.register('gender', {
                                onChange: () => {
                                    form.setValue('taxCode', '')
                                    form.trigger('gender')
                                }
                            })}
                        />
                    </Flexbox>
                    <Flexbox align="center" gap={1.5}>
                        <InputStatusIcon hasError={!!form.formState.errors.gender?.message} />
                        <InputHelpText error={form.formState.errors.gender?.message} />
                    </Flexbox>
                </Flexbox>
            </Flexbox>

            <Grid columns={6} gap={4} fullWidth>
                <GridItem spanColumn={isBornAbroad ? 3 : 2}>
                    <Controller
                        name="birthDate"
                        control={form.control}
                        render={({field}) => (
                            <InputDate
                                label={t('commons:birth_date_label')}
                                placeholder="GG/MM/AAAA"
                                errorMessage={form.formState.errors.birthDate?.message}
                                maxDate={dayjs().toDate()}
                                numMonths={1}
                                fullWidth
                                selectedDates={field.value ? [field.value] : []}
                                onChange={value => {
                                    field.onChange(value)
                                    form.setValue('taxCode', '')
                                    if (value) {
                                        form.setValue(
                                            'serviceType',
                                            dayjs().diff(value, 'years') > 17 ? 'adults' : 'evolutionary_age',
                                            {shouldDirty: true}
                                        )
                                    }
                                }}
                            />
                        )}
                    />
                </GridItem>

                {isBornAbroad ? (
                    <GridItem spanColumn={isBornAbroad ? 3 : 2}>
                        <Controller
                            control={form.control}
                            name="birthCountry"
                            render={({field}) => (
                                <InputSelectCountry
                                    label={t('commons:birth_country_label')}
                                    value={field.value}
                                    placeholder={t('commons:select')}
                                    errorMessage={form.formState.errors.birthCountry?.message}
                                    onChange={value => {
                                        field.onChange(value)
                                        form.setValue('taxCode', '')
                                    }}
                                />
                            )}
                        />
                    </GridItem>
                ) : (
                    <>
                        <GridItem spanColumn={2}>
                            <Controller
                                control={form.control}
                                name="birthCity"
                                render={({field}) => (
                                    <InputSelectCity
                                        label={
                                            isBornAbroad ? t('commons:birth_city_label') : t('commons:birth_town_label')
                                        }
                                        value={field.value}
                                        placeholder={t('commons:select')}
                                        errorMessage={form.formState.errors.birthCity?.message}
                                        onChange={value => {
                                            if (value?.province) {
                                                form.setValue('birthProvince', value.province)
                                                form.trigger('birthProvince')
                                            }

                                            field.onChange(value)
                                            form.trigger('birthCity')
                                            form.setValue('taxCode', '')
                                        }}
                                    />
                                )}
                            />
                        </GridItem>
                        <GridItem spanColumn={2}>
                            <Controller
                                control={form.control}
                                name="birthProvince"
                                render={({field}) => (
                                    <InputSelectProvince
                                        label={t('commons:birth_province_label')}
                                        value={field.value}
                                        placeholder={t('commons:select')}
                                        errorMessage={form.formState.errors.birthProvince?.message}
                                        onChange={value => {
                                            form.resetField('birthCity')
                                            field.onChange(value)
                                            form.setValue('taxCode', '')
                                        }}
                                    />
                                )}
                            />
                        </GridItem>
                    </>
                )}
            </Grid>

            <Checkbox
                id="isBornAbroad"
                label={t('commons:born_abroad')}
                {...form.register('isBornAbroad', {
                    onChange: value => {
                        if (value?.target.checked) {
                            form.setValue('birthCountry', null)
                            form.setValue('citizenship', null)
                            form.setValue('taxCodeType', null)
                        } else {
                            form.setValue('birthCountry', defaultCountryQuery.remappedData[0], {shouldDirty: true})
                            form.setValue('citizenship', defaultCountryQuery.remappedData[0], {shouldDirty: true})
                            form.setValue('taxCodeType', 'fiscal_code', {shouldDirty: true})
                        }
                        form.setValue('birthProvince', null)
                        form.setValue('birthCity', null)
                        form.setValue('taxCode', '')
                        form.clearErrors(['birthProvince', 'birthCity', 'birthCountry'])
                    }
                })}
            />

            {!defaultCountryQuery.data || !isBornAbroad ? (
                <Grid columns={3} gap={4} fullWidth>
                    <GridItem startColumn={1} endColumn={3} gap={4} direction="column" fullWidth>
                        <Flexbox direction="column" fullWidth gap={1.5}>
                            <Flexbox justify="space-between" align="center" fullWidth>
                                <Label>{t('commons:fiscal_code')}</Label>
                                <GenerateTaxCodeButton
                                    disabled={isCalculateTaxCodeDisabled}
                                    variant="tertiary"
                                    shape="text"
                                    onClick={onCalculateUserTaxCode}
                                >
                                    {t('commons:generate')}
                                </GenerateTaxCodeButton>
                            </Flexbox>
                            <InputTaxCode
                                type="text"
                                placeholder={t('commons:tax_code_placeholder')}
                                errorMessage={
                                    !calculatePatientTaxCode.isLoading ? form.formState.errors.taxCode?.message : ''
                                }
                                {...form.register('taxCode')}
                            />
                        </Flexbox>
                    </GridItem>
                </Grid>
            ) : (
                <Grid columns={4} gap={4} fullWidth>
                    <GridItem spanColumn={1}>
                        <Controller
                            control={form.control}
                            name="citizenship"
                            render={({field}) => (
                                <InputSelectCountry
                                    label={t('commons:citizenship')}
                                    value={field.value}
                                    placeholder={t('commons:select')}
                                    errorMessage={form.formState.errors.citizenship?.message}
                                    onChange={value => {
                                        field.onChange(value)
                                        form.setValue('taxCode', '')
                                        if (value?.name == 'Italia') {
                                            form.setValue('taxCodeType', 'fiscal_code', {shouldDirty: true})
                                            form.clearErrors('taxCode')
                                        }
                                    }}
                                />
                            )}
                        />
                    </GridItem>
                    <GridItem spanColumn={3} fullWidth>
                        {citizenship?.name != 'Italia' ? (
                            <Flexbox gap={2} align="flex-end" fullWidth>
                                <PatientTaxCodeField label={t('patients:stp_code')} />
                            </Flexbox>
                        ) : (
                            <Flexbox direction="column" fullWidth gap={1.5}>
                                <Flexbox justify="space-between" align="center" fullWidth>
                                    <Label>{t('commons:fiscal_code')}</Label>
                                    <GenerateTaxCodeButton
                                        disabled={isCalculateTaxCodeDisabled}
                                        variant="tertiary"
                                        shape="text"
                                        onClick={onCalculateUserTaxCode}
                                    >
                                        {t('commons:generate')}
                                    </GenerateTaxCodeButton>
                                </Flexbox>
                                <InputTaxCode
                                    type="text"
                                    placeholder={t('commons:tax_code_placeholder')}
                                    errorMessage={
                                        !calculatePatientTaxCode.isLoading ? form.formState.errors.taxCode?.message : ''
                                    }
                                    {...form.register('taxCode')}
                                />
                            </Flexbox>
                        )}
                    </GridItem>
                </Grid>
            )}

            <Grid columns={3} gap={4} fullWidth>
                <GridItem spanColumn={1} gap={2} direction="column">
                    <Controller
                        control={form.control}
                        name="maritalStatus"
                        render={({field, formState}) => (
                            <InputSelect
                                label={t('commons:marital_status')}
                                value={field.value ?? undefined}
                                optionsMap={maritalStatusesToLabel}
                                onChange={field.onChange}
                                errorMessage={formState.errors.maritalStatus?.message}
                            />
                        )}
                    />
                </GridItem>
                <GridItem spanColumn={1} gap={2} direction="column">
                    <Controller
                        control={form.control}
                        name="education"
                        render={({field, formState}) => (
                            <InputSelect
                                label={t('commons:qualification')}
                                value={field.value ?? undefined}
                                optionsMap={patientEducationToLabel}
                                onChange={field.onChange}
                                errorMessage={formState.errors.education?.message}
                            />
                        )}
                    />
                </GridItem>
                <GridItem spanColumn={1} gap={2} direction="column">
                    <Controller
                        control={form.control}
                        name="occupation"
                        render={({field, formState}) => (
                            <InputSelect
                                label={t('patients:occupation')}
                                value={field.value ?? undefined}
                                optionsMap={patientOccupationToLabel}
                                onChange={field.onChange}
                                errorMessage={formState.errors.occupation?.message}
                            />
                        )}
                    />
                </GridItem>
            </Grid>
        </Flexbox>
    )
}
